import { create } from 'zustand';

/**
 * App-wide event register — every module reports what happened here, and
 * the TopBar Events center renders it. One vocabulary for the whole app:
 *
 *   - `module`  — who did it (pilot, assets, sitl, diva, settings, system).
 *   - `robot`   — when a robot is involved, its id + display name.
 *   - `summary` — one operator-readable line, lowercase, no trailing period.
 *
 * Session-only ring buffer (newest first) — events are a live feed, not an
 * audit log; the Hub's audit trail is the durable record.
 */
export type AppModule = 'pilot' | 'assets' | 'sitl' | 'diva' | 'settings' | 'system';

export type AppEventSeverity = 'info' | 'warning' | 'critical';

export interface AppEvent {
  id: string;
  ts: number;
  module: AppModule;
  severity: AppEventSeverity;
  summary: string;
  robot_id?: string;
  robot_name?: string;
}

const MAX_EVENTS = 300;

interface AppEventsStore {
  events: AppEvent[];
  push: (e: Omit<AppEvent, 'id' | 'ts'>) => void;
  clear: () => void;
}

export const useAppEvents = create<AppEventsStore>((set) => ({
  events: [],
  push: (e) =>
    set((s) => ({
      events: [
        { ...e, id: `ev-${Math.random().toString(36).slice(2, 10)}`, ts: Date.now() },
        ...s.events,
      ].slice(0, MAX_EVENTS),
    })),
  clear: () => set({ events: [] }),
}));

/** Convenience for non-React call sites (libs, stores, socket handlers). */
export function recordEvent(
  module: AppModule,
  summary: string,
  opts: { severity?: AppEventSeverity; robotId?: string; robotName?: string } = {},
): void {
  useAppEvents.getState().push({
    module,
    summary,
    severity: opts.severity ?? 'info',
    robot_id: opts.robotId,
    robot_name: opts.robotName,
  });
}
